import React from 'react'
import {
  CheckoutPlanAmountPerMonth,
  CheckoutPlanBottomRow,
  CheckoutPlanDescription,
  CheckoutPlanDetailsEditPlan,
  CheckoutPlanDetailsStyles,
  CheckoutPlanDetailsTitle,
  CheckoutPlanDetailsTopRow,
  CheckoutPlanPrice,
  CheckoutPlanSeparator,
  CheckoutPlanTotalAmount,
  CheckoutPlanTotalTitle,
  CheckoutPlanDetailsRow,
  CheckoutPlanVisitsAmount,
  CheckoutPlanBundleRow,
  CheckoutPlanBundleDescription,
  CheckoutPlanBundlePrice,
  CheckoutOverageDisclaimer,
  CheckoutCouponRow,
  CheckoutCouponDescription,
  CheckoutCouponPrice,
  CheckoutPlanMonthlyTitle,
  CheckoutPlanMonthlyAmount,
  CheckoutPlanMonthlyWrapper,
  CheckoutPlanTotalWrapper,
  PaymentPlanTitle,
  CheckoutPlanUpperSeparator,
  CheckoutPlanTotalPrice,
  CheckoutFirstPayment,
  ToasterWrapper,
} from 'Features/checkout/components/CheckoutPlanDetails/CheckoutPlanDetails.styles'
import { useTranslation } from 'react-i18next'
import { getAmountWithCurrency } from 'Utils/currencyHelper'
import { Plan } from 'Features/pricing/types/Plan'
import { useNavigate } from 'react-router-dom'
import { CurrenciesTypes } from 'Types/Currencies'
import { COUPON_TYPE } from './CheckOutPlanDetails.model'
import { CouponType } from 'Features/checkout/components/Coupon/Coupon'
import { Toast, Tooltip, theme } from '@clickcease/cc-client-components-lib'
import { useVerifyUrlCodeFromParams } from 'Features/auth/hooks/useVerifyUrlCodeFromParams'
import { usePaymentsFlowSplit } from 'Hooks/split/usePaymentsFlow'

type CheckoutPlanDetailsProps = {
  planName: string
  currency: CurrenciesTypes
  amount: number
  allowedVisits?: number
  paidMarketingPlan?: Plan
  botMitigationPlan?: Plan
  bundle?: boolean
  reactivation?: boolean
  couponObject: CouponType
  toggleMonthlyState: boolean
  partner: boolean
  monthlyInstallmentAmount: number
  couponValidityStatus: string
  billingCycleMonths?: number
}

export const CheckoutPlanDetails = ({
  planName,
  amount,
  allowedVisits,
  currency,
  bundle,
  paidMarketingPlan,
  botMitigationPlan,
  couponObject,
  toggleMonthlyState,
  partner,
  monthlyInstallmentAmount,
  couponValidityStatus,
  reactivation,
  billingCycleMonths,
}: CheckoutPlanDetailsProps) => {
  const { t } = useTranslation('checkout')
  const isFeatureEnabled = usePaymentsFlowSplit()
  const installmentsPlan = '12_INSTALLMENTS'
  const { getSessionStorageItem } = useVerifyUrlCodeFromParams()

  const renderCoupon = getSessionStorageItem('cpncd')
  const currencySymbol = getAmountWithCurrency(currency, undefined)
  const navigate = useNavigate()
  const DISCOUNT = 0.85
  const MonthlyDiscount = 0.25
  const PercentageCoupon = couponObject.valid && couponObject.couponType === COUPON_TYPE.Percentage
  const FixedCoupon = couponObject.valid && couponObject.couponType === COUPON_TYPE.Fixed

  const getMonthlyCost = (amount) => {
    return Math.round(amount / 12)
  }

  const yearlyDiscount = Math.round(
    (getMonthlyCost(paidMarketingPlan?.amount) + getMonthlyCost(botMitigationPlan?.amount)) *
      (1 - DISCOUNT) *
      12
  )

  const interval = '/y'

  const getAmount = (isMonthly = false): number => {
    let sum: number

    if (bundle) {
      sum = ((paidMarketingPlan?.amount || 0) + (botMitigationPlan?.amount || 0)) * DISCOUNT
      monthlyInstallmentAmount = monthlyInstallmentAmount * DISCOUNT
    } else {
      sum = amount
    }
    if ((partner || couponObject) && !toggleMonthlyState) {
      if (PercentageCoupon) {
        const discount = couponObject.amount / 100
        sum = sum - sum * discount
      }
      if (FixedCoupon) {
        sum = sum - couponObject.amount
      }
    }
    return isMonthly ? getMonthlyCost(sum) : Math.round(sum)
  }

  const getCouponCheckOutPrice = (): string => {
    let CouponCheckoutPrice: string

    if (bundle) {
      if (PercentageCoupon) {
        const discountedMonthly = Math.round(
          (((paidMarketingPlan?.amount || 0) + (botMitigationPlan?.amount || 0)) *
            DISCOUNT *
            (couponObject.amount / 100)) /
            12
        )
        const discountedTotal = Math.round(
          ((paidMarketingPlan?.amount || 0) + (botMitigationPlan?.amount || 0)) *
            DISCOUNT *
            (couponObject.amount / 100)
        )
        CouponCheckoutPrice = `(-${currencySymbol}${discountedMonthly}/m) -${currencySymbol}${discountedTotal}`
      } else if (FixedCoupon) {
        CouponCheckoutPrice = `-${currencySymbol}${couponObject.amount}`
      } else {
        CouponCheckoutPrice = `-${currencySymbol}${couponObject.amount}`
      }
    } else {
      if (PercentageCoupon) {
        const discountedMonthly = Math.round((amount * couponObject.amount) / 100 / 12)
        const discountedTotal = Math.round((amount * couponObject.amount) / 100)
        CouponCheckoutPrice = `(-${currencySymbol}${discountedMonthly}/m) -${currencySymbol}${discountedTotal}`
      } else {
        CouponCheckoutPrice = `-${currencySymbol}${couponObject.amount}`
      }
    }

    return CouponCheckoutPrice
  }
  const installmentsMonthlyPrice = getAmount(true)
  const totalMonthlyForInstallmentSubscription = monthlyInstallmentAmount * 12
  const installmentPrice = Math.round(totalMonthlyForInstallmentSubscription * 0.06)
  const installmentFeeWithDiscount =
    couponObject?.amount && !toggleMonthlyState
      ? Math.round((installmentPrice * (100 - couponObject?.amount)) / 100)
      : installmentPrice

  const getCheckOutPrice = (
    bundle,
    toggleMonthlyState,
    amount: number,
    discount,
    interval: string
  ) => {
    if (bundle) {
      return `${paidMarketingPlan?.amount as number}${interval}`
    } else {
      return `${amount?.toLocaleString()}${interval}`
    }
  }

  const checkOutPrice = getCheckOutPrice(
    bundle,
    toggleMonthlyState,
    amount,
    MonthlyDiscount,
    interval
  ).replace(/\B(?=(\d{3})+(?!\d))/g, ',')

  const planTotalPrice = toggleMonthlyState
    ? (installmentsMonthlyPrice * 12 + Math.round(installmentFeeWithDiscount)).toLocaleString()
    : getAmount().toLocaleString()

  const couponIsValidAndProvidesADiscount =
    couponValidityStatus === 'valid' && couponObject.amount !== 0

  const planSeparator =
    couponIsValidAndProvidesADiscount ||
    (couponValidityStatus === 'invalidMonthly' && !toggleMonthlyState && !bundle && renderCoupon)

  return (
    <CheckoutPlanDetailsStyles>
      <CheckoutPlanDetailsTopRow>
        <CheckoutPlanDetailsTitle>{t('plan_overview')}</CheckoutPlanDetailsTitle>
        <CheckoutPlanDetailsEditPlan
          onClick={() => {
            navigate('/pricing', { state: { reactivation, billingCycleMonths } })
          }}
        >
          {t('edit_plan')}
        </CheckoutPlanDetailsEditPlan>
      </CheckoutPlanDetailsTopRow>
      <CheckoutPlanDetailsRow>
        <CheckoutPlanDescription>
          {bundle && paidMarketingPlan?.planType
            ? t(`paidmarketing_plan_${paidMarketingPlan?.planType.toLowerCase()}`)
            : t(planName.toLowerCase())}

          <CheckoutPlanVisitsAmount>
            {t('up_to_visits', {
              visits: (allowedVisits as number) ?? paidMarketingPlan?.visitsAllowed,
            })}
          </CheckoutPlanVisitsAmount>
        </CheckoutPlanDescription>
        <CheckoutPlanPrice>
          {currencySymbol}
          {checkOutPrice}
        </CheckoutPlanPrice>
      </CheckoutPlanDetailsRow>
      {bundle && (
        <>
          <CheckoutPlanDetailsRow>
            <CheckoutPlanDescription>
              {botMitigationPlan?.planType &&
                t(`botzapping_plan_${botMitigationPlan?.planType.toLowerCase()}`)}
              <CheckoutPlanVisitsAmount>
                {t('up_to_visits', {
                  visits: botMitigationPlan?.visitsAllowed as number,
                })}
              </CheckoutPlanVisitsAmount>
            </CheckoutPlanDescription>
            <CheckoutPlanPrice>
              {currencySymbol}
              {botMitigationPlan?.amount.toLocaleString()}
              {interval}
            </CheckoutPlanPrice>
          </CheckoutPlanDetailsRow>
          {isFeatureEnabled === installmentsPlan && (
            <>
              {!toggleMonthlyState ? <CheckoutPlanSeparator /> : <CheckoutPlanUpperSeparator />}

              <CheckoutPlanBundleRow>
                <CheckoutPlanBundleDescription>
                  {t('bundle_discount', { percent: '15' })}
                </CheckoutPlanBundleDescription>
                <CheckoutPlanBundlePrice>
                  {`-${currencySymbol}${yearlyDiscount.toLocaleString()}`}
                </CheckoutPlanBundlePrice>
              </CheckoutPlanBundleRow>
            </>
          )}
        </>
      )}
      {planSeparator && (
        <>
          <CheckoutPlanUpperSeparator></CheckoutPlanUpperSeparator>
        </>
      )}
      {couponIsValidAndProvidesADiscount ? (
        <CheckoutCouponRow>
          <CheckoutCouponDescription>
            {PercentageCoupon
              ? `${couponObject.amount}% ${t('coupon_discount')}`
              : t('coupon_discount')}
          </CheckoutCouponDescription>
          <CheckoutCouponPrice>{getCouponCheckOutPrice()}</CheckoutCouponPrice>
        </CheckoutCouponRow>
      ) : null}
      {couponValidityStatus === 'invalidMonthly' && (renderCoupon || partner) ? (
        <ToasterWrapper>
          <Toast type="failure" customIcon={'warning'}>
            {t('bundle_coupon_error_monthly')}
          </Toast>
        </ToasterWrapper>
      ) : null}
      <CheckoutPlanSeparator />
      <CheckoutPlanBottomRow>
        {isFeatureEnabled === installmentsPlan && toggleMonthlyState && (
          <>
            <CheckoutPlanMonthlyWrapper>
              <PaymentPlanTitle>
                {t('payment_plan')}
                <Tooltip
                  tooltiptContent={
                    <>
                      {t('one_time_fee')}
                      <br />
                      {t('fee_charged')}
                    </>
                  }
                  trigerStyle={{
                    style: {
                      color: theme.colors.gray.value,
                      marginLeft: '4px',
                      position: 'relative',
                      top: '3px',
                    },
                    width: '16px',
                  }}
                ></Tooltip>
              </PaymentPlanTitle>

              <PaymentPlanTitle>
                {currencySymbol}
                {installmentFeeWithDiscount.toLocaleString()}
              </PaymentPlanTitle>
            </CheckoutPlanMonthlyWrapper>
            <CheckoutPlanMonthlyWrapper>
              <CheckoutPlanMonthlyTitle>{t('monthly_payment_title')}</CheckoutPlanMonthlyTitle>
              <CheckoutPlanMonthlyAmount>
                {currencySymbol}
                {installmentsMonthlyPrice.toLocaleString()}
              </CheckoutPlanMonthlyAmount>
            </CheckoutPlanMonthlyWrapper>
          </>
        )}
        <CheckoutPlanTotalWrapper>
          <CheckoutPlanTotalTitle>{t('total_annual_pricing')}</CheckoutPlanTotalTitle>
          <CheckoutPlanTotalAmount>
            {toggleMonthlyState ? (
              <CheckoutPlanTotalPrice />
            ) : (
              <CheckoutPlanAmountPerMonth>
                ({currencySymbol}
                {installmentsMonthlyPrice.toLocaleString()}
                /m)
              </CheckoutPlanAmountPerMonth>
            )}
            <CheckoutPlanTotalAmount>
              {currencySymbol}
              {planTotalPrice}
            </CheckoutPlanTotalAmount>
          </CheckoutPlanTotalAmount>
        </CheckoutPlanTotalWrapper>
        {toggleMonthlyState && (
          <>
            <CheckoutPlanTotalWrapper>
              <CheckoutFirstPayment>
                {t('next_billing_payment')} | {currencySymbol}
                {(installmentsMonthlyPrice + installmentFeeWithDiscount).toLocaleString()}
              </CheckoutFirstPayment>
            </CheckoutPlanTotalWrapper>
          </>
        )}
      </CheckoutPlanBottomRow>
      <CheckoutOverageDisclaimer>{t('exceeding_disclaimer')}</CheckoutOverageDisclaimer>
    </CheckoutPlanDetailsStyles>
  )
}
